import React from "react";
import classNames from "classnames";
import ReactModalWrap from "../../../UI/Modals/ReactModalWrap/ReactModalWrap";
import type {
  OpeningHoursDay,
  OpeningHoursResponse,
} from "../../../../types/shared";
import styles from "./OpeningHoursModal.module.scss";

interface OpeningHoursModalProps {
  isOpen: boolean;
  onClose: () => void;
  openingHoursResponse?: OpeningHoursResponse | null;
  pharmacyName?: string;
}

// Local Serbian day labels (proper diacritics). Intentionally not shared with
// PharmacyDetails.tsx, which is unrelated admin UI with its own quirks.
const DAYS: ReadonlyArray<{ key: OpeningHoursDay; label: string }> = [
  { key: "monday", label: "Ponedeljak" },
  { key: "tuesday", label: "Utorak" },
  { key: "wednesday", label: "Sreda" },
  { key: "thursday", label: "Četvrtak" },
  { key: "friday", label: "Petak" },
  { key: "saturday", label: "Subota" },
  { key: "sunday", label: "Nedelja" },
] as const;

// Align "today" with the backend's Europe/Belgrade timezone instead of
// relying on device-local time.
const getCurrentDayKey = (): OpeningHoursDay => {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    timeZone: "Europe/Belgrade",
  })
    .format(new Date())
    .toLowerCase() as OpeningHoursDay;
};

// valid string -> trimmed; "Ne radi" -> as-is; null/undefined/empty/whitespace -> "/"
const formatDayValue = (value?: string | null): string => {
  if (!value || !value.trim()) return "/";
  return value.trim();
};

// True when the response carries at least one usable day value.
// Used to suppress the trigger on rows whose endpoint did not enrich
// openingHoursResponse (e.g. transaction-sourced rows), where an all-"/"
// modal would misrepresent the pharmacy as having no hours.
// "Ne radi" is a non-empty string and therefore counts as real data.
export const hasOpeningHoursData = (
  openingHoursResponse?: OpeningHoursResponse | null,
): boolean => {
  if (!openingHoursResponse) return false;
  return DAYS.some(({ key }) => {
    const value = openingHoursResponse[key];
    return typeof value === "string" && value.trim().length > 0;
  });
};

const OpeningHoursModal: React.FC<OpeningHoursModalProps> = ({
  isOpen,
  onClose,
  openingHoursResponse,
  pharmacyName,
}) => {
  const currentDayKey = getCurrentDayKey();

  return (
    <ReactModalWrap isOpen={isOpen} close={onClose}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>Radno vreme apoteke</h3>
          {pharmacyName && (
            <p className={styles.pharmacyName}>{pharmacyName}</p>
          )}
        </div>

        <div className={styles.modalBody}>
          {DAYS.map((day) => {
            const isToday = day.key === currentDayKey;
            return (
              <div
                key={day.key}
                className={classNames(styles.dayRow, {
                  [styles.dayRowToday]: isToday,
                })}
              >
                <span className={styles.dayLabel}>{day.label}</span>
                <span className={styles.dayValue}>
                  {formatDayValue(openingHoursResponse?.[day.key])}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </ReactModalWrap>
  );
};

export default OpeningHoursModal;
